import {
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CForm,
  CFormInput,
  CFormSelect,
  CFormCheck,
  CRow,
  CCol,
  CBadge,
  CFormLabel,
  CFormSwitch,
  CButtonGroup,
} from "@coreui/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  submitOnboardingData,
  fetchPaymentMethods,
  fetchAccountStatus,
  fetchOnboardingList,
} from "../../features/seller/sellerSlice";
import {
  fetchUserProfile,
  fetchUserAddresses,
} from "../../features/user/userSlice";
import { toast } from "react-toastify";
import AppToast from "../../components/seller/AppToast";
import { deriveVerificationState } from "../../utils/verificationState";
import { Country, State, City } from "country-state-city";
import {
  getOnboardingStorage,
  saveSection,
  clearSection,
} from "../../utils/onboardingStorage";

// ================= DOCUMENT OPTIONS =================
const documentOptions = {
  individual: [
    { value: "au_passport", label: "Australian Passport" },
    { value: "driver_license", label: "Driving License" },
    { value: "foreign_passport", label: "Foreign Passport" },
    { value: "medicare_card", label: "Medicare Card (Secondary ID)" },
    { value: "bank_statement", label: "Bank Statement (Address Proof)" },
    { value: "utility_bill", label: "Utility Bill (Address Proof)" },
    { value: "other", label: "Other" },
  ],
  company: [
    { value: "abn_extract", label: "ABN Extract / ABN Details" },
    { value: "asic_company_extract", label: "ASIC Company Extract" },
    {
      value: "certificate_incorporation",
      label: "Certificate of Incorporation",
    },
    { value: "gst_registration", label: "GST Registration (if applicable)" },
    { value: "director_id", label: "Director ID (if applicable)" },
    {
      value: "authorized_signatory_id",
      label: "Authorized Signatory ID (Passport / Licence)",
    },
    { value: "other", label: "Other" },
  ],
};

// ================= VALIDATION SCHEMAS =================
const addressValidationSchema = Yup.object().shape({
  addressLine1: Yup.string().required("Address Line 1 is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  postalCode: Yup.string().required("Postal Code is required"),
  country: Yup.string().required("Country is required"),
  addressType: Yup.string().required("Address type is required"),
});

const kycValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  documents: Yup.array()
    .min(1, "At least one document is required for verification")
    .of(
      Yup.object().shape({
        type: Yup.string().required("Document Type is required"),
        customType: Yup.string().when("type", {
          is: "other",
          then: (schema) => schema.required("Please specify the document type"),
          otherwise: (schema) => schema.notRequired(),
        }),
        file: Yup.mixed().notRequired(),
      }),
    ),
  isConsent: Yup.boolean()
    .required("You must agree to the consent.")
    .oneOf([true], "You must agree to the consent."),
});

const payoutValidationSchema = Yup.object().shape({
  accountName: Yup.string().required("Account Holder Name is required"),
  bankName: Yup.string().required("Bank Name is required"),
  bsb: Yup.string().required("BSB Number is required"),
  accountNumber: Yup.string().required("Account Number is required"),
  accountType: Yup.string().required("Account Type is required"),
});

const VerificationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section") || "kyc";

  // Form states
  const [kycType, setKycType] = useState("individual");
  const [name, setName] = useState("");
  const [documents, setDocuments] = useState([
    { type: "", customType: "", file: null },
    { type: "", customType: "", file: null },
  ]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isConsent, setIsConsent] = useState(false);

  const [payoutMethod, setPayoutMethod] = useState("");
  const [bankDetails, setBankDetails] = useState({
    accountName: "",
    bankName: "",
    bsb: "",
    accountNumber: "",
    accountType: "",
    branchName: "",
    proofFile: null,
    proofUrl: null,
  });

  const [address, setAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    addressType: "Home",
    setDefault: false,
  });

  // Status tracking
  const [kycStatus, setKycStatus] = useState(null);
  const [bankStatus, setBankStatus] = useState(null);
  const [stripeConnectedFromApi, setStripeConnectedFromApi] = useState(0);

  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");

  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.user);
  const { data: accountStatus } = useSelector(
    (state) => state.seller.accountStatus,
  );

  const isStripeConnected = useMemo(
    () =>
      profile?.stripe_account_id &&
      accountStatus?.charges_enabled &&
      accountStatus?.payouts_enabled &&
      accountStatus?.details_submitted,
    [profile, accountStatus],
  );

  // ================= COMPUTED FORM STATE =================
  const verificationState = useMemo(
    () =>
      deriveVerificationState({
        kycStatus,
        bankStatus,
        stripeConnected: stripeConnectedFromApi,
        payoutMethod,
      }),
    [kycStatus, bankStatus, stripeConnectedFromApi, payoutMethod],
  );

  // ================= EDITABILITY LOGIC =================
  const isKycEditable =
    kycStatus === "rejected" || kycStatus === "pending" || !kycStatus;

  // const isPayoutEditable =
  //   !payoutMethod || // No payout method selected yet
  //   bankStatus === "rejected" || // Bank details rejected
  //   (kycStatus === "approved" && (!bankStatus || bankStatus === null)); // KYC approved but no payout status yet


  // const isPayoutEditable =
  // !bankStatus || // Bank is null (no bank details added yet)
  // bankStatus === "rejected" || // Bank details rejected  
  // !payoutMethod || // No payout method selected yet
  // kycStatus === "approved" || // KYC is approved
  // kycStatus === "pending"; // KYC is pending

  const isPayoutEditable =
  !bankStatus || // Bank is null (no bank details added yet)
  bankStatus === "rejected" || // Bank details rejected
  bankStatus === "pending" || // Bank details pending
  (!payoutMethod && (kycStatus === "approved" || kycStatus === "pending")); // No payout method and KYC approved/pending

  const isAddressEditable = true;

  const isPayoutRadioEditable = isPayoutEditable;

  // ================= LOAD DATA ON MOUNT =================
  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchAccountStatus());
    dispatch(fetchUserAddresses());

    dispatch(fetchOnboardingList())
      .unwrap()
      .then((response) => {
        const data = response;

        // Capture backend statuses
        setKycStatus(data?.kyc?.status || null);
        setBankStatus(data?.bank?.verification_status || null);
        setStripeConnectedFromApi(data?.stripe_connected || 0);

        // Pre-fill KYC data
        if (data?.kyc) {
          setKycType(data.kyc.kyc_type || "individual");
          setName(data.kyc.name || "");

          if (data.kyc.is_consent === 1 || data.kyc.is_consent === true) {
            setIsConsent(true);
          }

          // Extract existing documents
          const existingDocs = [];
          for (let i = 1; i <= 4; i++) {
            const docType = data.kyc[`document_type${i}`];
            const docUrl =
              data.kyc[`document_url${i}`] || data.kyc[`document_path${i}`];
            if (docType || docUrl) {
              existingDocs.push({
                type: docType || "Document",
                url: docUrl,
              });
            }
          }

          setExistingDocuments(existingDocs);
        }

        // Pre-fill Address data
        if (data?.address) {
          setAddress({
            addressLine1: data.address.addressLine1 || "",
            addressLine2: data.address.addressLine2 || "",
            city: data.address.city || "",
            state: data.address.state || "",
            postalCode: data.address.postal_code || "",
            country: data.address.region || "",
            addressType: data.address.address_type || "Home",
            setDefault: data.address.is_default === 1,
          });
        }

        // Pre-fill Payout data - ALWAYS set payoutMethod (even if null)
        const methodFromApi = data?.payout?.method;
        setPayoutMethod(methodFromApi || "");

        if (methodFromApi === "bank" && data?.bank) {
          setBankDetails({
            accountName: data.bank.account_holder_name || "",
            bankName: data.bank.bank_name || "",
            bsb: data.bank.bsb || "",
            accountNumber: data.bank.account_number || "",
            accountType: data.bank.account_type || "",
            branchName: data.bank.branch_name || "",
            proofFile: null,
            proofUrl:
              data.bank.document_url || data.bank.bank_document_url || null,
          });
        }
      })
      .catch((error) => {
        console.error("Failed to fetch onboarding data:", error);
      });
  }, [dispatch]);

  // ================= VALIDATION =================
  const sectionValidity = useMemo(() => {
    const validity = { kyc: false, payout: false, address: false };

    // KYC Validation
    try {
      const hasNewFile = documents.some((d) => d.file);

      if (!name || !isConsent) {
        validity.kyc = false;
      } else if (hasNewFile) {
        kycValidationSchema.validateSync({ name, documents, isConsent });
        validity.kyc = true;
      } else if (existingDocuments.length > 0 && kycStatus !== "rejected") {
        // If documents already submitted and not rejected, consider valid
        validity.kyc = true;
      } else {
        validity.kyc = false;
      }
    } catch (e) {
      validity.kyc = false;
    }

    // Payout Validation
    try {
      if (!payoutMethod) {
        validity.payout = false;
      } else if (payoutMethod === "stripe") {
        validity.payout = stripeConnectedFromApi === 1;
      } else if (payoutMethod === "bank") {
        payoutValidationSchema.validateSync(bankDetails);
        // If editing, require new proof file; if already approved, don't require it
        if (bankStatus === "rejected" || !bankStatus) {
          validity.payout = !!bankDetails.proofFile;
        } else {
          validity.payout = !!bankDetails.proofUrl || !!bankDetails.proofFile;
        }
      }
    } catch (e) {
      validity.payout = false;
    }

    // Address Validation
    try {
      addressValidationSchema.validateSync(address);
      validity.address = true;
    } catch (e) {
      validity.address = false;
    }

    return validity;
  }, [
    name,
    documents,
    isConsent,
    existingDocuments,
    kycStatus,
    payoutMethod,
    stripeConnectedFromApi,
    bankDetails,
    bankStatus,
    address,
  ]);

  // ================= OVERALL VALIDATION =================
  const validation = useMemo(() => {
    return {
      kyc: sectionValidity.kyc,
      payout: sectionValidity.payout,
      address: sectionValidity.address,
      // Only require sections that are editable to be valid
      all:
        (isKycEditable ? sectionValidity.kyc : true) &&
        (isPayoutEditable ? sectionValidity.payout : true) &&
        (isAddressEditable ? sectionValidity.address : true),
    };
  }, [sectionValidity, isKycEditable, isPayoutEditable, isAddressEditable]);

  // ================= DOCUMENT HELPERS =================
  const updateDocument = useCallback((index, field, value) => {
    // Validate file type/size when setting a file
    if (field === "file") {
      const file = value;
      if (file) {
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/pdf",
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (!allowedTypes.includes(file.type)) {
          toast.error(
            "Invalid file type. Only JPG, JPEG, PNG and PDF are allowed.",
          );
          return;
        }
        if (file.size > maxSize) {
          toast.error("File too large. Maximum allowed size is 5MB.");
          return;
        }
      }
    }

    setDocuments((prev) => {
      const updated = [...prev];

      if (!updated[index]) {
        updated[index] = { type: "", customType: "", file: null };
      }

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      if (field === "type" && value !== "other") {
        updated[index].customType = "";
      }

      return updated;
    });
  }, []);

  const getDocumentLabel = useCallback((kycType, value) => {
    const option = documentOptions[kycType].find((opt) => opt.value === value);
    return option ? option.label : value;
  }, []);

  // ================= SAVE KYC (LocalStorage + Database) =================
  const handleSaveKyc = async () => {
    if (!sectionValidity.kyc) {
      toast.error("Please complete KYC details");
      return;
    }

    setSaving(true);

    try {
      // 1️⃣ Save to localStorage first
      saveSection("kyc", {
        name,
        kycType,
        isConsent,
        documentTypes: documents.map((d) => ({
          type: d.type,
          customType: d.customType,
          fileName: d.file?.name || null,
        })),
      });

      // 2️⃣ Prepare FormData for API (ONLY KYC DATA)
      const formData = new FormData();
      formData.append("name", name);
      formData.append("is_consent", isConsent ? 1 : 0);

      documents.forEach((doc, index) => {
        if (!doc.file) return;

        formData.append(`kyc[${index}][kycType]`, kycType);
        formData.append(
          `kyc[${index}][documentType]`,
          doc.type === "other"
            ? doc.customType
            : getDocumentLabel(kycType, doc.type)
        );
        formData.append(`kyc[${index}][file]`, doc.file);
      });

      // 3️⃣ Call API - Only sends KYC data
      await dispatch(submitOnboardingData(formData)).unwrap();

      // 4️⃣ Refresh data from backend
      await dispatch(fetchOnboardingList());

      toast.success("KYC saved successfully");
    } catch (error) {
      console.error("Error saving KYC:", error);
      toast.error(error?.message || "Failed to save KYC");
    } finally {
      setSaving(false);
    }
  };

  // ================= SAVE PAYOUT (LocalStorage + Database) =================
  const handleSavePayout = async () => {
    if (!sectionValidity.payout) {
      toast.error("Complete payout details");
      return;
    }

    setSaving(true);

    try {
      // 1️⃣ Save to localStorage first
      saveSection("payout", {
        method: payoutMethod,
        bankDetails: {
          accountName: bankDetails.accountName,
          bankName: bankDetails.bankName,
          bsb: bankDetails.bsb,
          accountNumber: bankDetails.accountNumber,
          accountType: bankDetails.accountType,
          branchName: bankDetails.branchName,
          proofFileName: bankDetails.proofFile?.name || null,
        },
      });

      // 2️⃣ Prepare FormData for API (ONLY PAYOUT DATA)
      const formData = new FormData();
      formData.append("payout[method]", payoutMethod);

      if (payoutMethod === "bank") {
        formData.append(
          "payout[bankDetails][account_holder_name]",
          bankDetails.accountName
        );
        formData.append(
          "payout[bankDetails][bank_name]",
          bankDetails.bankName
        );
        formData.append(
          "payout[bankDetails][branch_name]",
          bankDetails.branchName || ""
        );
        formData.append(
          "payout[bankDetails][account_type]",
          bankDetails.accountType
        );
        formData.append(
          "payout[bankDetails][account_number]",
          bankDetails.accountNumber
        );
        formData.append("payout[bankDetails][bsb]", bankDetails.bsb);

        if (bankDetails.proofFile) {
          formData.append(
            "payout[bankDetails][bank_document]",
            bankDetails.proofFile
          );
        }
      }

      // 3️⃣ Call API - Only sends Payout data
      await dispatch(submitOnboardingData(formData)).unwrap();

      // 4️⃣ Refresh data from backend
      await dispatch(fetchOnboardingList());

      toast.success("Payout saved successfully");
    } catch (error) {
      console.error("Error saving payout:", error);
      toast.error(error?.message || "Failed to save payout");
    } finally {
      setSaving(false);
    }
  };

  // ================= SAVE ADDRESS (LocalStorage + Database) =================
  const handleSaveAddress = async () => {
    if (!sectionValidity.address) {
      toast.error("Complete address");
      return;
    }

    setSaving(true);

    try {
      // 1️⃣ Save to localStorage first
      saveSection("address", address);

      // 2️⃣ Prepare FormData for API (ONLY ADDRESS DATA)
      const formData = new FormData();
      formData.append("address[addressLine1]", address.addressLine1);
      formData.append("address[addressLine2]", address.addressLine2 || "");
      formData.append("address[city]", address.city);
      formData.append("address[state]", address.state);
      formData.append("address[postal_code]", address.postalCode);
      formData.append("address[address_type]", address.addressType);
      formData.append("address[region]", address.country);
      formData.append("address[isDefault]", address.setDefault ? 1 : 0);

      // 3️⃣ Call API - Only sends Address data
      await dispatch(submitOnboardingData(formData)).unwrap();

      // 4️⃣ Refresh data from backend
      await dispatch(fetchOnboardingList());

      toast.success("Address saved successfully");
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error(error?.message || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectToStripe = useCallback(async () => {
    try {
      setConnecting(true);
      const response = await dispatch(fetchPaymentMethods()).unwrap();
      const url = response?.stripe_onboarding_url;

      if (url) {
        window.open(url, "_blank");
      } else {
        setToastType("warning");
        setToastMessage("Stripe onboarding URL not available.");
        setToastVisible(true);
      }
    } catch (error) {
      console.error("Failed to fetch Stripe onboarding URL:", error);
      setToastType("danger");
      setToastMessage("Error connecting to Stripe.");
      setToastVisible(true);
    } finally {
      setConnecting(false);
    }
  }, [dispatch]);

  // ===== LOAD LOCAL STORAGE DATA (SAFE VERSION) =====
  useEffect(() => {
    const stored = getOnboardingStorage();

    // ✅ Load draft ONLY if API has not filled values yet

    // KYC
    if (!name && stored?.kyc) {
      setName(stored.kyc.name || "");
      setKycType(stored.kyc.kycType || "individual");
      setIsConsent(!!stored.kyc.isConsent);
    }

    // PAYOUT
    if (!payoutMethod && stored?.payout) {
      setPayoutMethod(stored.payout.method || "");
      setBankDetails(stored.payout.bankDetails || {});
    }

    // ADDRESS
    if (!address.addressLine1 && stored?.address) {
      setAddress(stored.address);
    }
  }, [name, payoutMethod, address.addressLine1]);

  return (
    <CCard>
      <CCardHeader>
        <strong>Verify profile</strong>
      </CCardHeader>

      <CCardBody>
        <AppToast
          visible={toastVisible}
          setVisible={setToastVisible}
          type={toastType}
          message={toastMessage}
        />

        {/* Section Navigation */}
        <div className="mb-4">
          <CButtonGroup role="group">
            <CButton
              color={section === "kyc" ? "primary" : "outline-primary"}
              onClick={() => navigate("?section=kyc")}
            >
              <div className="d-flex align-items-center gap-2">
                <span>Verification Details</span>
                {sectionValidity.kyc && (
                  <CBadge color="success" shape="rounded-pill">
                    ✓
                  </CBadge>
                )}
              </div>
            </CButton>
            <CButton
              color={section === "payout" ? "primary" : "outline-primary"}
              onClick={() => navigate("?section=payout")}
            >
              <div className="d-flex align-items-center gap-2">
                <span>Payout Setup</span>
                {sectionValidity.payout && (
                  <CBadge color="success" shape="rounded-pill">
                    ✓
                  </CBadge>
                )}
              </div>
            </CButton>
            <CButton
              color={section === "address" ? "primary" : "outline-primary"}
              onClick={() => navigate("?section=address")}
            >
              <div className="d-flex align-items-center gap-2">
                <span>Address</span>
                {sectionValidity.address && (
                  <CBadge color="success" shape="rounded-pill">
                    ✓
                  </CBadge>
                )}
              </div>
            </CButton>
          </CButtonGroup>
        </div>

        {/* Section Content */}
        {section === "kyc" && (
          <KycFormSection
            kycType={kycType}
            setKycType={setKycType}
            name={name}
            setName={setName}
            documents={documents}
            existingDocuments={existingDocuments}
            isConsent={isConsent}
            setIsConsent={setIsConsent}
            updateDocument={updateDocument}
            isEditable={isKycEditable}
            kycStatus={kycStatus}
            onSave={handleSaveKyc}
            saving={saving}
          />
        )}

        {section === "payout" && (
          <PayoutFormSection
            payoutMethod={payoutMethod}
            setPayoutMethod={setPayoutMethod}
            bankDetails={bankDetails}
            setBankDetails={setBankDetails}
            isStripeConnected={isStripeConnected}
            stripeConnectedFromApi={stripeConnectedFromApi}
            connecting={connecting}
            handleConnectToStripe={handleConnectToStripe}
            isPayoutEditable={isPayoutEditable}
            isPayoutRadioEditable={isPayoutRadioEditable}
            bankStatus={bankStatus}
            onSave={handleSavePayout}
            saving={saving}
          />
        )}

        {section === "address" && (
          <AddressFormSection
            address={address}
            setAddress={setAddress}
            isEditable={isAddressEditable}
            onSave={handleSaveAddress}
            saving={saving}
          />
        )}
      </CCardBody>
    </CCard>
  );
};

// ================= KYC FORM SECTION =================
const KycFormSection = ({
  kycType,
  setKycType,
  name,
  setName,
  documents,
  existingDocuments,
  isConsent,
  setIsConsent,
  updateDocument,
  isEditable,
  kycStatus,
  onSave,
  saving,
}) => {
  return (
    <CForm className="mt-3">
      {kycStatus === "rejected" && (
        <div
          className="mb-4 p-3 rounded alert-danger"
          style={{
            borderLeft: "4px solid #dc3545",
            backgroundColor: "#f8d7da",
          }}
        >
          <h6 className="fw-bold text-danger mb-1">Verification Rejected</h6>
          <p className="small mb-0 text-muted">
            Your profile verification submission has been rejected. Please
            review your documents and resubmit.
          </p>
        </div>
      )}

      <div
        className="mb-4 p-3 rounded"
        style={{
          borderLeft: "4px solid #007bff",
          backgroundColor: "#f0f7ff",
        }}
      >
        <h6 className="fw-bold text-primary mb-1">Instructions:</h6>
        <ul className="small mb-0 text-muted ps-3">
          {existingDocuments.length > 0 && kycStatus !== "rejected" ? (
            <>
              <li>Your documents have been submitted for verification.</li>
              <li>Status: Pending Review</li>
              <li>You will be notified once verification is complete.</li>
            </>
          ) : (
            <>
              <li>
                Please upload <strong>two document</strong> to proceed with
                verification.
              </li>
              <li>
                Ensure the documents are clear and all details are visible.
              </li>
              <li>File type: JPG, JPEG, PNG, PDF (max 5MB each)</li>
            </>
          )}
        </ul>
      </div>

      <CRow>
        <CCol md={6}>
          <CFormSelect
            label="Verification Type"
            value={kycType}
            onChange={(e) => setKycType(e.target.value)}
            disabled={!isEditable}
          >
            <option value="individual">Individual</option>
            <option value="company">Company</option>
          </CFormSelect>
        </CCol>

        <CCol md={6}>
          <CFormInput
            label={kycType === "individual" ? "Full Name" : "Company Name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isEditable}
          />
        </CCol>
      </CRow>

      {/* Document Upload Section */}
      {existingDocuments &&
      existingDocuments.length > 0 &&
      kycStatus !== "rejected" ? (
        <div className="mt-4">
          <h6 className="fw-bold mb-3">Submitted Documents</h6>
          {existingDocuments.map((doc, index) => (
            <CCard key={index} className="mb-3">
              <CCardBody>
                <CRow>
                  <CCol md={6}>
                    <label className="form-label">Document Type</label>
                    <div className="fw-semibold">{doc.type}</div>
                  </CCol>
                  <CCol
                    md={6}
                    className="d-flex align-items-end justify-content-end"
                  >
                    <CButton
                      color="info"
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(doc.url, "_blank")}
                    >
                      View Document
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          ))}
        </div>
      ) : (
        isEditable && (
          <>
            <h6 className="fw-bold mt-4 mb-3">Upload Documents</h6>

            {documents.map((doc, index) => (
              <CCard key={index} className="mt-3 p-3">
                <CRow>
                  <CCol md={4}>
                    <CFormSelect
                      label={`Document Type ${index + 1}`}
                      value={doc.type}
                      onChange={(e) =>
                        updateDocument(index, "type", e.target.value)
                      }
                    >
                      <option value="">Select Document Type</option>
                      {documentOptions[kycType]
                        .filter(
                          (opt) =>
                            opt.value === "other" ||
                            opt.value === doc.type ||
                            !documents.some(
                              (d, idx) => idx !== index && d.type === opt.value
                            )
                        )
                        .map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                    </CFormSelect>
                  </CCol>

                  {doc.type === "other" && (
                    <CCol md={4}>
                      <CFormInput
                        label="Specify Document Type"
                        value={doc.customType}
                        onChange={(e) =>
                          updateDocument(index, "customType", e.target.value)
                        }
                      />
                    </CCol>
                  )}

                  <CCol md={doc.type === "other" ? 4 : 6}>
                    <CFormInput
                      type="file"
                      label="Upload Document"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) =>
                        updateDocument(index, "file", e.target.files[0])
                      }
                    />
                    {doc.file && (
                      <small className="text-success">✓ {doc.file.name}</small>
                    )}
                  </CCol>
                </CRow>
              </CCard>
            ))}
          </>
        )
      )}

      <div
        className="mt-4 p-3 rounded"
        style={{
          border: "2px solid #ffc107",
          backgroundColor: "#fff8e1",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}
      >
        <div className="d-flex align-items-start gap-2">
          <input
            type="checkbox"
            id="isConsent"
            className="form-check-input mt-1"
            style={{ transform: "scale(1.2)" }}
            checked={isConsent}
            onChange={(e) => setIsConsent(e.target.checked)}
            disabled={!isEditable}
          />
          <label
            htmlFor="isConsent"
            className="form-check-label"
            style={{
              fontSize: "1rem",
              color: "#222",
              fontWeight: "600",
              lineHeight: "1.5",
            }}
          >
            {kycType === "company"
              ? "I confirm that I am the authorised representative of the company to handle the transactions - sale, purchase, financial transaction etc."
              : "I confirm that I am authorized to handle the transactions - sale, purchase, financial transaction etc."}
          </label>
        </div>
      </div>

      {isEditable && (
        <div className="mt-4 text-end">
          <CButton color="primary" onClick={onSave} disabled={saving}>
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              "Save KYC"
            )}
          </CButton>
        </div>
      )}
    </CForm>
  );
};

// ================= PAYOUT FORM SECTION =================
const PayoutFormSection = ({
  payoutMethod,
  setPayoutMethod,
  bankDetails,
  setBankDetails,
  isStripeConnected,
  connecting,
  isPayoutEditable,
  isPayoutRadioEditable,
  handleConnectToStripe,
  bankStatus,
  stripeConnectedFromApi,
  onSave,
  saving,
}) => {
  const actuallyStripeConnected = stripeConnectedFromApi === 1;
  const canEditPayoutDetails = isPayoutEditable;

  return (
    <div className="mt-3">
      {bankStatus === "rejected" && (
        <div
          className="mb-4 p-3 rounded alert-danger"
          style={{
            borderLeft: "4px solid #dc3545",
            backgroundColor: "#f8d7da",
          }}
        >
          <h6 className="fw-bold text-danger mb-1">
            Bank Verification Rejected
          </h6>
          <p className="small mb-0 text-muted">
            Your bank details were rejected. Please review and resubmit.
          </p>
        </div>
      )}

      <CFormCheck
        type="radio"
        name="payout"
        label="Stripe Connect"
        checked={payoutMethod === "stripe"}
        onChange={() => setPayoutMethod("stripe")}
        disabled={!isPayoutRadioEditable}
      />

      {payoutMethod === "stripe" && (
        <>
          <CCard className="mt-2 p-3 border">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="text-muted">
                {actuallyStripeConnected
                  ? "Your Stripe account is connected."
                  : "To receive payments, please connect your Stripe account."}
              </span>

              {actuallyStripeConnected ? (
                <CBadge color="success">Stripe Connected</CBadge>
              ) : (
                <CButton
                  color="info"
                  onClick={handleConnectToStripe}
                  disabled={connecting || !canEditPayoutDetails}
                >
                  {connecting ? "Connecting..." : "Connect to Stripe"}
                </CButton>
              )}
            </div>
          </CCard>

          {isPayoutEditable && actuallyStripeConnected && (
            <div className="text-end mt-4">
              <CButton color="primary" onClick={onSave} disabled={saving}>
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Saving...
                  </>
                ) : (
                  "Save Payout"
                )}
              </CButton>
            </div>
          )}
        </>
      )}

      <CFormCheck
        className="mt-3"
        type="radio"
        name="payout"
        label="Bank Details"
        checked={payoutMethod === "bank"}
        onChange={() => setPayoutMethod("bank")}
        disabled={!isPayoutRadioEditable}
      />

      {payoutMethod === "bank" && (
        <CCard className="mt-2 p-3 border">
          <CRow className="g-3">
            <CCol md={6}>
              <CFormInput
                label="Account Holder Name"
                value={bankDetails.accountName}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    accountName: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              />
            </CCol>

            <CCol md={6}>
              <CFormInput
                label="Bank Name"
                value={bankDetails.bankName}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    bankName: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              />
            </CCol>

            <CCol md={6}>
              <CFormInput
                label="BSB Number"
                value={bankDetails.bsb}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    bsb: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              />
            </CCol>

            <CCol md={6}>
              <CFormInput
                label="Account Number"
                value={bankDetails.accountNumber}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    accountNumber: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              />
            </CCol>

            <CCol md={6}>
              <CFormSelect
                label="Account Type"
                value={bankDetails.accountType}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    accountType: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              >
                <option value="">Select Account Type</option>
                <option value="saving">Savings</option>
                <option value="current">Checking</option>
              </CFormSelect>
            </CCol>

            <CCol md={6}>
              <CFormInput
                label="Branch Location (Optional)"
                value={bankDetails.branchName}
                onChange={(e) =>
                  setBankDetails({
                    ...bankDetails,
                    branchName: e.target.value,
                  })
                }
                disabled={!canEditPayoutDetails}
              />
            </CCol>

            <CCol md={12}>
              {bankDetails.proofUrl && !canEditPayoutDetails ? (
                <div className="mb-3">
                  <CFormLabel>Bank Proof (Submitted)</CFormLabel>
                  <div>
                    <CButton
                      color="info"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(bankDetails.proofUrl, "_blank")
                      }
                    >
                      View Document
                    </CButton>
                  </div>
                </div>
              ) : (
                canEditPayoutDetails && (
                  <>
                    <CFormInput
                      type="file"
                      label="Upload Bank Proof (Required)"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) =>
                        setBankDetails({
                          ...bankDetails,
                          proofFile: e.target.files[0],
                        })
                      }
                    />
                    {bankDetails.proofFile && (
                      <small className="text-success d-block mt-1">
                        ✓ File selected: {bankDetails.proofFile.name}
                      </small>
                    )}
                  </>
                )
              )}
            </CCol>

            {isPayoutEditable && (
              <CCol md={12}>
                <div className="text-end mt-4">
                  <CButton color="primary" onClick={onSave} disabled={saving}>
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Payout"
                    )}
                  </CButton>
                </div>
              </CCol>
            )}
          </CRow>
        </CCard>
      )}
    </div>
  );
};

// ================= ADDRESS FORM SECTION =================
const AddressFormSection = ({ address, setAddress, isEditable, onSave, saving }) => {
  return (
    <CForm className="mt-3">
      <CRow className="mb-3">
        <CCol md={6}>
          <CFormLabel>Address Line 1</CFormLabel>
          <CFormInput
            value={address.addressLine1}
            onChange={(e) =>
              setAddress({ ...address, addressLine1: e.target.value })
            }
            disabled={!isEditable}
          />
        </CCol>
        <CCol md={6}>
          <CFormLabel>Address Line 2</CFormLabel>
          <CFormInput
            value={address.addressLine2}
            onChange={(e) =>
              setAddress({ ...address, addressLine2: e.target.value })
            }
            disabled={!isEditable}
          />
        </CCol>
      </CRow>

      {/* Country / State / City */}
      <CRow className="mb-3">
        {/* COUNTRY */}
        <CCol md={3}>
          <CFormLabel>Country</CFormLabel>
          <CFormSelect
            value={address.country || ""}
            disabled={!isEditable}
            onChange={(e) =>
              setAddress({
                ...address,
                country: e.target.value,
                state: "",
                city: "",
              })
            }
          >
            <option value="">Select Country</option>
            {Country.getAllCountries().map((country) => (
              <option key={country.isoCode} value={country.isoCode}>
                {country.name}
              </option>
            ))}
          </CFormSelect>
        </CCol>

        {/* STATE */}
        <CCol md={3}>
          <CFormLabel>State</CFormLabel>
          <CFormSelect
            value={address.state || ""}
            disabled={!isEditable || !address.country}
            onChange={(e) =>
              setAddress({
                ...address,
                state: e.target.value,
                city: "",
              })
            }
          >
            <option value="">Select State</option>
            {State.getStatesOfCountry(address.country || "").map((state) => (
              <option key={state.isoCode} value={state.isoCode}>
                {state.name}
              </option>
            ))}
          </CFormSelect>
        </CCol>

        {/* CITY */}
        <CCol md={3}>
          <CFormLabel>City</CFormLabel>
          <CFormSelect
            value={address.city || ""}
            disabled={!isEditable || !address.state}
            onChange={(e) =>
              setAddress({
                ...address,
                city: e.target.value,
              })
            }
          >
            <option value="">Select City</option>
            {City.getCitiesOfState(
              address.country || "",
              address.state || ""
            ).map((city, index) => (
              <option key={index} value={city.name}>
                {city.name}
              </option>
            ))}
          </CFormSelect>
        </CCol>
        <CCol md={2}>
          <CFormLabel>Postal Code</CFormLabel>
          <CFormInput
            value={address.postalCode}
            onChange={(e) =>
              setAddress({ ...address, postalCode: e.target.value })
            }
            disabled={!isEditable}
          />
        </CCol>
      </CRow>

      <CRow className="mb-3">
        <CCol md={6}>
          <CFormLabel>Address Type</CFormLabel>
          <CFormSelect
            value={address.addressType}
            onChange={(e) =>
              setAddress({ ...address, addressType: e.target.value })
            }
            disabled={!isEditable}
          >
            <option value="">Select Type</option>
            <option value="office">Office</option>
            <option value="warehouse">Warehouse</option>
            <option value="home">Home</option>
          </CFormSelect>
        </CCol>

        <CCol md={6} className="d-flex align-items-end">
          <div className="d-flex align-items-center w-100 mb-2">
            <CFormSwitch
              id="defaultSwitch"
              checked={address.setDefault}
              onChange={(e) =>
                setAddress({
                  ...address,
                  setDefault: e.target.checked,
                })
              }
              className="me-2 mb-0"
              disabled={!isEditable}
            />
            <label htmlFor="defaultSwitch" className="mb-0 fw-semibold">
              Set as default address
            </label>
          </div>
        </CCol>
      </CRow>

      {isEditable && (
        <div className="text-end mt-4">
          <CButton color="primary" onClick={onSave} disabled={saving}>
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              "Save Address"
            )}
          </CButton>
        </div>
      )}
    </CForm>
  );
};

export default VerificationForm;